import type { AssistantState } from '../../types/runtime-state';
import type { ConversationMessage } from '../../types/conversation-message';

const secondsTimestampThreshold = 100_000_000_000;

type AssistantTurnTimestampItem =
  | { type: 'message'; message: Pick<ConversationMessage, 'timestamp'> }
  | { type: 'state'; state: Pick<AssistantState, 'phaseStartedAt'> };

export function assistantTimestampToDate(timestamp: number): Date | null {
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  const timestampMs =
    Math.abs(timestamp) < secondsTimestampThreshold
      ? timestamp * 1000
      : timestamp;
  const date = new Date(timestampMs);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function assistantTimestampToISOString(
  timestamp: number
): string | null {
  return assistantTimestampToDate(timestamp)?.toISOString() ?? null;
}

export function formatAssistantMessageTimestamp(
  timestamp: number,
  locale?: string,
  options: { now?: Date; timeZone?: string } = {}
): string | null {
  const date = assistantTimestampToDate(timestamp);
  if (!date) {
    return null;
  }

  const now = options.now ?? new Date();
  const includeYear = date.getFullYear() !== now.getFullYear();
  return new Intl.DateTimeFormat(locale || undefined, {
    year: includeYear ? 'numeric' : undefined,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(date);
}

export function assistantTurnTimestamp(
  items: AssistantTurnTimestampItem[]
): number | undefined {
  let earliestTimestamp: number | undefined;
  let earliestTimeMs: number | undefined;

  for (const item of items) {
    const timestamp =
      item.type === 'message'
        ? item.message.timestamp
        : item.state.phaseStartedAt;
    const date = assistantTimestampToDate(timestamp);
    if (!date) {
      continue;
    }

    const timeMs = date.getTime();
    if (earliestTimeMs === undefined || timeMs < earliestTimeMs) {
      earliestTimestamp = timestamp;
      earliestTimeMs = timeMs;
    }
  }

  return earliestTimestamp;
}
